// Phase 85.A.63 — Hook auto-resume Bước 4 Thanh toán.
// Phase 85.A.64 — Fix path navigate sai gây 404.
//
// Mount tại top-level (App.jsx). Khi app load:
//   1. Đọc localStorage `pickleague:pending_payment_v1`.
//   2. Nếu KHÔNG có pending → no-op.
//   3. Nếu CÓ pending → query backend `/athlete/registrations/{rid}/payment-info`
//      để verify reg đó vẫn ở trạng thái chờ thanh toán (chưa paid + chưa cancel).
//      • paid/overpaid → clear marker, không redirect.
//      • cancelled / 404 → clear marker, không redirect.
//      • pending → redirect tới `/athlete/tournaments/{id}/register?event={eid}&resume=1`.
//   4. Chỉ redirect khi user ĐANG ở trang KHÁC trang đăng ký giải đó
//      (tránh ping-pong khi đã đang ở Bước 4).
//   5. Chỉ chạy 1 lần per session (ref guard).
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import {
  clearPendingPayment,
  readPendingPayment,
} from "../lib/pendingPaymentStorage";
import { useFeatureFlag } from "./useFeatureFlags";

export default function usePendingPaymentResume() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Compliance kill-switch — skip auto-resume when payment feature OFF.
  const { enabled: paymentEnabled } = useFeatureFlag("payment");
  const ran = useRef(false);

  useEffect(() => {
    // Chỉ chạy 1 lần khi user đã login.
    if (ran.current) return;
    if (!user?.id) return;
    // Skip resume when payment feature is disabled.
    if (!paymentEnabled) {
      clearPendingPayment();
      return;
    }
    ran.current = true;

    const pending = readPendingPayment();
    if (!pending) return;

    // Phase 85.A.64 — Path PHẢI khớp với React route trong App.js:
    //   `<Route path="/athlete/tournaments/:id/register" />` (line 611)
    // Trước đây dùng `/tournaments/{slug}/register` → 404. Sử dụng
    // tournament_id (UUID) thay vì slug để khớp với :id placeholder, vì
    // backend `/public/tournaments/{slug|id}` resolve cả 2 nhưng React
    // route chỉ accept 1 token. ID là canonical, an toàn nhất.
    const tidForRoute = pending.tournament_id || pending.tournament_slug;
    const targetPath = `/athlete/tournaments/${tidForRoute}/register`;
    // Tránh ping-pong khi user đang ở chính trang đó.
    if (location.pathname === targetPath) return;

    let cancelled = false;
    (async () => {
      try {
        // Verify reg vẫn pending — endpoint trả status + payment_status
        // + fresh sepay QR (Phase 83y.22).
        const r = await api.get(
          `/athlete/registrations/${pending.reg_id}/payment-info`,
        );
        if (cancelled) return;
        const data = r.data || {};
        const st = (data.status || "").toLowerCase();
        const pst = (data.payment_status || "").toLowerCase();
        // Đã paid → clear marker.
        if (pst === "paid" || pst === "overpaid" || st === "approved") {
          clearPendingPayment();
          return;
        }
        // Đã huỷ → clear marker.
        if (
          st === "cancelled"
          || st === "withdrawn"
          || st === "rejected"
          || st === "deleted"
        ) {
          clearPendingPayment();
          return;
        }
        // Còn pending → redirect Bước 4.
        const qs = new URLSearchParams({
          event: pending.event_id,
          resume: "1",
        });
        if (pending.invite_id) qs.set("invite", pending.invite_id);
        navigate(`${targetPath}?${qs.toString()}`, { replace: false });
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        // Reg không còn tồn tại hoặc không thuộc user → clear marker.
        if (status === 404 || status === 403) {
          clearPendingPayment();
        }
        // Lỗi khác (network) → giữ marker, lần sau thử lại.
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, paymentEnabled]);
}
